import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DateCalendar from './DateCalendar';

// Completed dates use ISO in form state; incomplete drafts remain visible for validation.
export default function DateField({ type, value, name, id, className, readOnly, disabled, required, min, max, onChange, onBlur, ...rest }) {
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const pickerValue = useRef(value || '');
  const [open, setOpen] = useState(false);
  const [blurValue, setBlurValue] = useState(undefined);
  const withTime = type === 'datetime-local';
  const isoFormat = withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD';
  const displayFormat = withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
  const current = value || '';
  const parsed = moment(current, isoFormat, true);
  const valid = parsed.isValid();
  const displayValue = valid ? parsed.format(displayFormat) : current;
  const label = rest['aria-label'] || name || id || 'Data';
  const emit = (nextValue) => onChange({ target: { name, id, value: nextValue, type } });
  const closePicker = restoreFocus => {
    setOpen(false);
    setBlurValue(pickerValue.current);
    if (restoreFocus) triggerRef.current.focus();
  };

  useEffect(() => {
    if (blurValue !== undefined) {
      onBlur({ target: { name, id, value: blurValue, type } });
      setBlurValue(undefined);
    }
  }, [blurValue, onBlur, name, id, type]);

  useEffect(() => {
    let message = '';
    if (current && !valid) message = withTime ? 'Podaj poprawną datę i godzinę: DD.MM.RRRR GG:mm.' : 'Podaj poprawną datę: DD.MM.RRRR.';
    else if (current && ((min && current < min) || (max && current > max))) message = 'Data jest poza dozwolonym zakresem.';
    inputRef.current.setCustomValidity(message);
  }, [current, valid, withTime, min, max]);

  return (
    <div className={`input-group date-field${className.includes('is-invalid') ? ' is-invalid' : ''}`}>
      <input
        {...rest}
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        className={className}
        value={displayValue}
        placeholder={withTime ? 'DD.MM.RRRR GG:mm' : 'DD.MM.RRRR'}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        autoComplete="off"
        onChange={(event) => {
          const next = moment(event.target.value, displayFormat, true);
          emit(next.isValid() ? next.format(isoFormat) : event.target.value);
        }}
        onBlur={() => onBlur({ target: { name, id, value: current, type } })}
      />
      {!readOnly && (
        <div className="input-group-append polish-date-picker">
          <button
            ref={triggerRef}
            type="button"
            className="input-group-text"
            aria-label={`Wybierz datę: ${label}`}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={disabled}
            onClick={() => {
              if (open) closePicker(false);
              else { pickerValue.current = current; setOpen(true); }
            }}
            onBlur={() => { if (!open) setBlurValue(current); }}
          >▦
          </button>
        </div>
      )}
      {open && !disabled && !readOnly && (
        <DateCalendar
          value={valid ? current : ''}
          withTime={withTime}
          min={min}
          max={max}
          anchor={triggerRef}
          label={label}
          onSelect={nextValue => {
            pickerValue.current = nextValue;
            emit(nextValue);
          }}
          onClose={closePicker}
        />
      )}
    </div>
  );
}

DateField.propTypes = {
  type: PropTypes.oneOf(['date', 'datetime-local']),
  value: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};
DateField.defaultProps = {
  type: 'date',
  value: '',
  name: undefined,
  id: undefined,
  className: 'form-control',
  readOnly: false,
  disabled: false,
  required: false,
  min: undefined,
  max: undefined,
  onChange: () => {},
  onBlur: () => {},
};
